import { useState, useEffect, useCallback, useMemo } from 'react';
import { Music, MoreVertical, Pencil, Trash, Plus, Filter, Search, ListPlus, ListMinus, Play, Pause, Clock3, FileX, Bell, Download, Disc, Timer, HeadphonesIcon, X, ChevronLeft, ChevronRight, Sparkles, Upload, Building2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { debounce } from 'lodash';
import SongUploader from '@/components/upload/SongUploader';
import { Song } from '@/types/song';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from 'react-router-dom';
import { useSelectedSongsStore } from '@/store/selectedSongsStore';
import axios from '@/lib/axios';
import { cn } from '@/lib/utils';
import { AddToPlaylistModal } from '@/components/playlists/AddToPlaylistModal';
import { RemoveFromPlaylistModal } from '@/components/playlists/RemoveFromPlaylistModal';
import { usePlayerStore } from '@/store/playerStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

// Metin formatlamak için utility fonksiyon
const formatTitle = (text: string | undefined | {name: string; _id: string}) => {
  if (!text || text === '-') return '-';
  
  // Eğer obje ise name özelliğini al
  if (typeof text === 'object' && text.name) {
    return formatTitleString(text.name);
  }
  
  // String ise normal işleme devam et
  return formatTitleString(String(text));
};

// String formatlamak için yardımcı fonksiyon
const formatTitleString = (text: string): string => {
  return text
    .split('_')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface PaginationData {
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

// Zaman formatlamak için yardımcı fonksiyon (en büyük birim mantığı)
const formatDuration = (seconds: number) => {
  if (isNaN(seconds) || seconds < 0) return '0 saniye';

  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  // 1 aydan fazla
  if (months > 0) {
    const remainingWeeks = Math.floor((days % 30) / 7);
    return remainingWeeks > 0 ? `${months} ay ${remainingWeeks} hafta` : `${months} ay`;
  }

  // 1 haftadan fazla
  if (weeks > 0) {
    const remainingDays = days % 7;
    return remainingDays > 0 ? `${weeks} hafta ${remainingDays} gün` : `${weeks} hafta`;
  }

  // 1 günden fazla
  if (days > 0) {
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days} gün ${remainingHours} saat` : `${days} gün`;
  }

  // 1 saatten fazla
  if (hours > 0) {
    const remainingMinutes = totalMinutes % 60;
    return remainingMinutes > 0 ? `${hours} saat ${remainingMinutes} dakika` : `${hours} saat`;
  }

  // Sadece dakika
  if (totalMinutes > 0) {
    return `${totalMinutes} dakika`;
  }

  // Sadece saniye
  return `${seconds} saniye`;
};

const formatFileSize = (bytes: number) => {
  if (isNaN(bytes) || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export default function MusicUploadPage() {
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [genres, setGenres] = useState<{_id: string, name: string}[]>([]);
  const [selectedGenre, setSelectedGenre] = useState<string>('');
  const [selectedSource, setSelectedSource] = useState<string>(''); // AI filtresi
  const [searchQuery, setSearchQuery] = useState<string>('');

  // AI generation state (super admin prompt-based generation)
  const [aiPrompt, setAiPrompt] = useState<string>('');
  const [aiIsInstrumental, setAiIsInstrumental] = useState<boolean>(false);
  const [aiGender, setAiGender] = useState<"" | "male" | "female" | "neutral">('');
  const [isAiGenerating, setIsAiGenerating] = useState<boolean>(false);
  // Aktif/bekleyen AI üretimleri — polling ile takip edilir
  const [pendingGenerations, setPendingGenerations] = useState<Array<{
    id: string;
    prompt: string;
    startedAt: number;
  }>>([]);
  const [pagination, setPagination] = useState<PaginationData>({
    total: 0,
    page: 1,
    totalPages: 1,
    hasMore: false
  });
  const [addToPlaylistOpen, setAddToPlaylistOpen] = useState<boolean>(false);
  const [removeFromPlaylistOpen, setRemoveFromPlaylistOpen] = useState<boolean>(false);
  const [showUploader, setShowUploader] = useState(false);
  const [editingSong, setEditingSong] = useState<Song | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    artist: '',
    album: '',
    genre: ''
  });
  
  // Oynatıcı durumunu store'dan al
  const { currentSong, isPlaying, playSong, pauseSong } = usePlayerStore();
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedSongs, addSong, removeSong, clearSelection } = useSelectedSongsStore();
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  // Backend'den istatistikleri getir
  const [songStats, setSongStats] = useState({
    total: 0,
    totalDuration: 0,
    totalSize: 0,
    avgDuration: 0,
    uniqueArtists: 0,
    uniqueAlbums: 0,
    uniqueGenres: 0,
    todayUploaded: 0
  });

  // İstatistikleri backend'den çek
  const fetchStatistics = async () => {
    try {
      const { data } = await axios.get('/songs/statistics');
      setSongStats(data);
    } catch (error) {
      console.error('İstatistik hatası:', error);
    }
  };

  useEffect(() => {
    fetchStatistics();
  }, []);

  // AI generation polling — bekleyen üretim varken her 5 sn'de:
  //  1. Tüm şarkı listesini yenile (manager'daki refetchInterval pattern'i)
  //  2. Her pending'in durumunu sorgula, tamamlananları listeden çıkar
  useEffect(() => {
    if (pendingGenerations.length === 0) return;

    const tick = async () => {
      // (1) Liste yenile (silent — loading state flash etmesin)
      fetchSongs(1, selectedGenre, searchQuery, selectedSource, true);

      // (2) Pending durumlarını kontrol et, tamamlananları çıkar
      const stillPending: typeof pendingGenerations = [];
      for (const gen of pendingGenerations) {
        try {
          const { data } = await axios.get(`/ai-music/${gen.id}/status`);
          const status = data?.data?.status || data?.status;
          if (status === 'completed') {
            toast({
              title: 'Müzik hazır',
              description: `"${gen.prompt.slice(0, 50)}${gen.prompt.length > 50 ? '...' : ''}" listeye eklendi`
            });
          } else if (status === 'failed') {
            toast({
              title: 'Üretim başarısız',
              description: gen.prompt.slice(0, 50),
              variant: 'destructive'
            });
          } else {
            stillPending.push(gen);
          }
        } catch {
          stillPending.push(gen);
        }
      }
      setPendingGenerations(stillPending);
    };

    const interval = setInterval(tick, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGenerations.length]);

  // İstatistik kartları için veri
  const statItems = useMemo(() => [
    {
      label: "Toplam Şarkı",
      value: songStats.total,
      icon: Music,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      info: "Sistemdeki tüm şarkılar"
    },
    {
      label: "Toplam Süre",
      value: formatDuration(songStats.totalDuration),
      icon: Timer,
      color: "text-amber-500",
      bgColor: "bg-amber-500/10",
      info: "Tüm şarkıların toplam süresi"
    },
    {
      label: "Ortalama Süre",
      value: formatDuration(songStats.avgDuration),
      icon: Clock3,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
      info: "Şarkı başına ortalama süre"
    },
    {
      label: "Bugün Yüklenen",
      value: songStats.todayUploaded,
      icon: FileX,
      color: "text-green-600",
      bgColor: "bg-green-500/10",
      info: "Bugün eklenen şarkılar"
    },
    {
      label: "Toplam Sanatçı",
      value: songStats.uniqueArtists,
      icon: HeadphonesIcon,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
      info: "Benzersiz sanatçı sayısı"
    },
    {
      label: "Toplam Albüm",
      value: songStats.uniqueAlbums,
      icon: Disc,
      color: "text-indigo-500",
      bgColor: "bg-indigo-500/10",
      info: "Benzersiz albüm sayısı"
    },
    {
      label: "Toplam Tür",
      value: songStats.uniqueGenres,
      icon: Bell,
      color: "text-cyan-500",
      bgColor: "bg-cyan-500/10",
      info: "Benzersiz müzik türü sayısı"
    },
    {
      label: "Toplam Boyut",
      value: formatFileSize(songStats.totalSize),
      icon: Download,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
      info: "Tüm şarkıların toplam dosya boyutu"
    },
  ], [songStats]);

  // Şarkıları getir
  const fetchSongs = async (
    page: number = 1,
    genre: string = selectedGenre,
    search: string = searchQuery,
    source: string = selectedSource,
    silent: boolean = false
  ) => {
    try {
      if (!silent) setLoading(true);
      const { data } = await axios.get('/songs', {
        params: {
          genre,
          search,
          source: source || undefined,
          page,
          limit: 40
        }
      });
      
      // Şarkı özelliklerinin doğru olduğundan emin ol
      const processedSongs = data.songs.map((song: Song) => {
        // Null veya undefined değerleri için varsayılan değerler kullan
        return {
          ...song,
          artist: song.artist || 'Bilinmeyen Sanatçı',
          album: song.album || '',
          genre: song.genre || 'Other',
          duration: song.duration || 0,
          fileSize: song.fileSize || 0,
          createdAt: song.createdAt || new Date().toISOString()
        };
      });
      
      // Toplam şarkı sayısını doğrudan API yanıtından alalım
      const totalSongs = typeof data.total === 'number' ? data.total : 
                       (data.pagination?.total || processedSongs.length);
      
      console.log("İşlenmiş şarkılar:", processedSongs);
      console.log("Toplam şarkı sayısı (API):", totalSongs);
      
      setSongs(processedSongs);
      setPagination({
        total: totalSongs,
        page: data.page || data.pagination?.page || 1,
        totalPages: data.totalPages || data.pagination?.totalPages || 1,
        hasMore: data.hasMore || data.pagination?.hasMore || false
      });
    } catch (error) {
      console.error('Şarkılar yüklenirken hata:', error);
      if (!silent) {
        toast({
          variant: "destructive",
          title: "Hata",
          description: "Şarkılar yüklenirken bir hata oluştu",
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Müzik türlerini getir - RADİKAL YENİ YAKLAŞIM
  const fetchGenres = async () => {
    try {
      console.log("Türler yükleniyor...");
      const { data } = await axios.get('/songs/genres');
      
      // Gelen veriyi kontrol edelim
      console.log("API'den gelen türler:", data);
      
      if (!Array.isArray(data)) {
        console.error("Türler dizisi değil:", data);
        setGenres([]);
        return;
      }
      
      // Türleri doğru formatta olduğundan emin olalım
      const processedGenres = data.map(genre => {
        // Eğer tür bir obje değilse veya _id/name özellikleri yoksa, düzeltelim
        if (typeof genre === 'string') {
          return { _id: genre, name: genre };
        } else if (genre && typeof genre === 'object') {
          // _id yoksa oluştur
          if (!genre._id && genre.name) {
            return { ...genre, _id: genre.name };
          }
          // name yoksa _id'yi kullan
          if (!genre.name && genre._id) {
            return { ...genre, name: genre._id };
          }
          return genre;
        }
        return { _id: 'unknown', name: 'Bilinmeyen' };
      }).filter(genre => genre && genre._id && genre.name); // Geçersiz türleri filtrele
      
      console.log("İşlenmiş türler:", processedGenres);
      setGenres(processedGenres);
      
      // Tür sayısını kontrol et
      if (processedGenres.length === 0) {
        console.warn("Yüklenen tür sayısı: 0");
        toast({
          variant: "destructive",
          title: "Uyarı",
          description: "Sistemde tanımlı müzik türü bulunamadı.",
        });
      } else {
        console.log(`Yüklenen tür sayısı: ${processedGenres.length}`);
      }
    } catch (err) {
      console.error('Türler getirilirken hata:', err);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Müzik türleri yüklenirken bir sorun oluştu",
      });
      setGenres([]); // Hata durumunda boş dizi
    }
  };

  // Tür değişikliği için handler (radikal yeni sürüm)
  const handleGenreChange = (genre: string) => {
    console.log(`Seçilen tür ID: "${genre}"`);

    // Tür bilgisini kontrol et
    if (genre) {
      const selectedGenreObj = genres.find(g => g._id === genre);
      console.log("Seçilen tür objesi:", selectedGenreObj);

      if (!selectedGenreObj) {
        console.warn(`ID'si "${genre}" olan tür bulunamadı!`);
      }
    }

    setSelectedGenre(genre);
    setPagination(prev => ({ ...prev, page: 1 })); // Tür değişince ilk sayfaya dön
    fetchSongs(1, genre, searchQuery, selectedSource);
    clearSelection(); // Tür değişince seçimleri temizle
  };

  // Kaynak değişikliği için handler (AI filtresi)
  const handleSourceChange = (source: string) => {
    console.log(`Seçilen kaynak: "${source}"`);
    setSelectedSource(source);
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSongs(1, selectedGenre, searchQuery, source);
    clearSelection();
  };

  // AI müzik üretimi (super admin)
  const handleAiGenerate = async () => {
    const trimmed = aiPrompt.trim();

    if (!trimmed) {
      toast({ title: 'Prompt gerekli', description: 'Lütfen bir prompt girin', variant: 'destructive' });
      return;
    }
    if (trimmed.length > 300) {
      toast({ title: 'Prompt çok uzun', description: 'Maksimum 300 karakter', variant: 'destructive' });
      return;
    }

    setIsAiGenerating(true);
    try {
      const response = await axios.post('/ai-music/admin-generate', {
        prompt: trimmed,
        isInstrumental: aiIsInstrumental,
        gender: aiGender || undefined
      });

      // Pending listesine tüm varyasyon ID'lerini ekle — backend her istekten 2 varyasyon üretiyor.
      const aiMusicIds: string[] = response.data?.data?.aiMusicIds || [response.data?.data?.aiMusicId].filter(Boolean);
      if (aiMusicIds.length > 0) {
        const now = Date.now();
        setPendingGenerations(prev => [
          ...prev,
          ...aiMusicIds.map(id => ({ id, prompt: trimmed, startedAt: now }))
        ]);
      }

      toast({
        title: 'Üretim başlatıldı',
        description: 'Şarkı hazırlanıyor, tamamlanınca listede görünecek'
      });

      setAiPrompt('');
    } catch (error: any) {
      toast({
        title: 'Üretim hatası',
        description: error.response?.data?.message || 'Bir hata oluştu',
        variant: 'destructive'
      });
    } finally {
      setIsAiGenerating(false);
    }
  };

  // Debounce ile arama
  const debouncedSearch = useCallback(
    debounce((search: string) => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchSongs(1, selectedGenre, search, selectedSource);
      clearSelection();
    }, 300),
    [selectedGenre, selectedSource]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    fetchGenres();
    fetchSongs();
  }, []);

  const handleUploadComplete = () => {
    fetchSongs();
    fetchStatistics(); // İstatistikleri yenile
    setShowUploader(false); // Modal'ı kapat
    toast({
      title: "Başarılı",
      description: "Şarkı başarıyla yüklendi",
      variant: "default",
      className: "bg-green-50 border-green-200",
    });
  };

  const handleDelete = async (songId: string) => {
    try {
      await axios.delete(`/songs/${songId}`);
      toast({
        title: "Başarılı",
        description: "Şarkı başarıyla silindi.",
        variant: "default",
        className: "bg-green-50 border-green-200",
      });
      fetchSongs();
      fetchStatistics(); // İstatistikleri yenile
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: err instanceof Error ? err.message : 'Şarkı silinirken bir hata oluştu',
        className: "bg-red-50 border-red-200 text-red-900",
      });
    }
  };

  // Toplu silme fonksiyonu
  const handleBulkDelete = async () => {
    if (selectedSongs.length === 0) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Lütfen silmek için şarkı seçin",
        className: "bg-red-50 border-red-200 text-red-900",
      });
      return;
    }

    try {
      // Seçili şarkıları paralel olarak sil
      await Promise.all(selectedSongs.map(song =>
        axios.delete(`/songs/${song._id}`)
      ));

      toast({
        title: "Başarılı",
        description: `${selectedSongs.length} şarkı başarıyla silindi.`,
        variant: "default",
        className: "bg-green-50 border-green-200",
      });

      clearSelection();
      fetchSongs();
      fetchStatistics(); // İstatistikleri yenile
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: err instanceof Error ? err.message : 'Şarkılar silinirken bir hata oluştu',
        className: "bg-red-50 border-red-200 text-red-900",
      });
    }
  };

  // Şarkı düzenleme fonksiyonu
  const handleEdit = (song: Song) => {
    console.log("Düzenlenen şarkı:", song);
    
    // Tür bilgisini direkt string olarak alıyoruz
    // Tabloda gösterilen değer zaten doğru olduğuna göre
    const tableGenre = document.querySelector(`[data-song-id="${song._id}"] .genre-column`)?.textContent?.trim() || '';
    console.log("Tablodan alınan tür:", tableGenre);
    
    setEditingSong(song);
    setEditFormData({
      name: song.name || '',
      artist: song.artist || '',
      album: song.album || '',
      genre: tableGenre // Tabloda gösterilen değeri kullanıyoruz
    });
    
    setShowEditModal(true);
  };

  useEffect(() => {
    if (editingSong?.genre) {
      console.log("Düzenlenen şarkının türü:", editingSong.genre);
    }
  }, [editingSong]);

  // Şarkı güncelleme fonksiyonu
  const handleSaveEdit = async () => {
    if (!editingSong) return;
    
    console.log("Kaydetme öncesi form verileri:", editFormData);
    
    // Genre değerini sunucuya gönderilecek doğru formata dönüştür
    const genreToSend = editFormData.genre;
    console.log("Gönderilecek tür:", genreToSend);
    
    try {
      await axios.put(`/songs/${editingSong._id}`, {
        ...editFormData,
        genre: genreToSend
      });
      // Düzenlenen şarkıyı güncelle
      const updatedSongs = songs.map(song => 
        song._id === editingSong._id ? 
          { ...song, ...editFormData, duration: song.duration, fileSize: song.fileSize } : 
          song
      );
      setSongs(updatedSongs);
      
      setShowEditModal(false);
      toast({
        title: "Başarılı",
        description: "Şarkı bilgileri güncellendi",
        variant: "default",
        className: "bg-green-50 border-green-200",
      });
      fetchSongs(); // Listeyi yenile
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: err instanceof Error ? err.message : 'Şarkı güncellenirken bir hata oluştu',
        className: "bg-red-50 border-red-200 text-red-900",
      });
    }
  };

  const handleCheckboxChange = (
    song: Song, 
    checked: boolean, 
    index: number, 
    event: React.MouseEvent<HTMLInputElement> | Event
  ) => {
    // Shift key basılı olup olmadığını kontrol et
    const shiftKeyPressed = 'shiftKey' in event ? event.shiftKey : false;
    
    console.log('Checkbox clicked:', {
      songTitle: song.name,
      checked,
      index,
      shiftKey: shiftKeyPressed,
      lastSelectedIndex
    });

    if (shiftKeyPressed && lastSelectedIndex !== null && lastSelectedIndex !== index) {
      console.log('Range selection activated', {
        start: Math.min(lastSelectedIndex, index),
        end: Math.max(lastSelectedIndex, index)
      });
      
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      
      const songsInRange = songs.slice(start, end + 1);
      console.log('Songs in range:', songsInRange.map(s => s.name));
      
      if (checked) {
        // Her şarkı için kontrol ederek ekle
        songsInRange.forEach((song: Song) => {
          // Şarkı zaten seçili değilse ekle
          if (!selectedSongs.some(selected => selected._id === song._id)) {
            addSong(song);
          }
        });
      } else {
        // Seçili olanları kaldır
        songsInRange.forEach((song: Song) => removeSong(song._id));
      }
    } else {
      if (checked) {
        // Tekli seçimde de kontrol et
        if (!selectedSongs.some(selected => selected._id === song._id)) {
          addSong(song);
        }
      } else {
        removeSong(song._id);
      }
    }
    
    setLastSelectedIndex(index);
  };

  const handleCreatePlaylist = () => {
    if (selectedSongs.length === 0) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Lütfen en az bir şarkı seçin",
        className: "bg-red-50 border-red-200 text-red-900",
      });
      return;
    }
    // AI Üretim filtresi aktifken oluşturulan playlist'i AI olarak işaretle
    navigate("/playlists/new", {
      state: { isAi: selectedSource === 'ai-generated' }
    });
  };

  const handleAddToPlaylist = () => {
    if (selectedSongs.length === 0) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Lütfen en az bir şarkı seçin",
        className: "bg-red-50 border-red-200 text-red-900",
      });
      return;
    }
    setAddToPlaylistOpen(true);
  };

  const handleAddToPlaylistSuccess = () => {
    setAddToPlaylistOpen(false);
    toast({
      title: "Başarılı",
      description: `${selectedSongs.length} şarkı playlist'e eklendi`,
      className: "bg-green-50 border-green-200",
    });
    clearSelection();
  };

  const handleRemoveFromPlaylistSuccess = () => {
    setRemoveFromPlaylistOpen(false);
    toast({
      title: "Başarılı",
      description: `${selectedSongs.length} şarkı playlist'ten çıkarıldı`,
      className: "bg-green-50 border-green-200",
    });
    clearSelection();
  };

  const handleRemoveFromPlaylist = () => {
    if (selectedSongs.length === 0) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Lütfen en az bir şarkı seçin",
        className: "bg-red-50 border-red-200 text-red-900",
      });
      return;
    }
    setRemoveFromPlaylistOpen(true);
  };

  const handlePageChange = (newPage: number) => {
    fetchSongs(newPage);
    clearSelection();
  };

  // Seçili türdeki tüm şarkıları getir
  const fetchAllSongsByGenre = async (genre: string) => {
    try {
      const response = await axios.get(`/songs`, {
        params: {
          genre,
          limit: 1000, // Yüksek limit ile tüm şarkıları al
          page: 1
        }
      });
      return response.data.songs;
    } catch (error) {
      console.error('Error fetching all songs:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Şarkılar yüklenirken bir hata oluştu",
      });
      return [];
    }
  };

  // Seçili türdeki tüm şarkıları seç
  const handleSelectAllByGenre = async () => {
    if (!selectedGenre) {
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Lütfen önce bir tür seçin",
      });
      return;
    }

    const allSongs = await fetchAllSongsByGenre(selectedGenre);
    clearSelection();
    allSongs.forEach((song: Song) => addSong(song));

    toast({
      title: "Başarılı",
      description: `${allSongs.length} şarkı seçildi`,
      className: "bg-green-50 border-green-200",
    });
  };

  // Şarkı zamanını formatla
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Backend zaten filtreliyor, client-side filtreleme gereksiz
  const filteredSongs = songs;

  if (loading) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-6 p-8 pt-6">
          {/* Hero Section Skeleton */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <div className="h-56 bg-gradient-to-r from-pink-700 to-pink-600 p-8">
              <div className="flex justify-between items-start relative w-full h-full">
                <div className="space-y-3 flex-1">
                  <Skeleton className="h-4 w-40 bg-white/20" />
                  <Skeleton className="h-9 w-80 bg-white/30" />
                  <Skeleton className="h-16 w-[500px] bg-white/20" />
                </div>
                <Skeleton className="w-40 h-40 rounded-lg bg-white/10" />
              </div>
            </div>
          </div>

          {/* İstatistik kartları skeleton - Birinci satır kartları */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    {/* Arka plan dekoratif gradient */}
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>

                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[120px] mb-2" />
                          <Skeleton className="h-9 w-[70px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>

                      {/* Alt bilgi çubuğu */}
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[120px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* İstatistik kartları skeleton - İkinci satır kartları */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    {/* Arka plan dekoratif gradient */}
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>

                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[120px] mb-2" />
                          <Skeleton className="h-9 w-[70px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>

                      {/* Alt bilgi çubuğu */}
                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[120px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filtreler skeleton */}
          <Card className="p-6">
            <div className="flex flex-col md:flex-row gap-4 justify-between">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-[120px]" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full md:w-1/2">
                <div className="space-y-2">
                  <Skeleton className="h-5 w-[100px]" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-5 w-[100px]" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-5 w-[100px]" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>
            </div>
          </Card>

          {/* Şarkıları listesi skeleton */}
          <Card className="rounded-lg border overflow-hidden bg-white shadow-md">
            <div className="p-6 border-b border-slate-100">
              <div className="flex justify-between">
                <Skeleton className="h-5 w-[150px]" />
                <div className="flex gap-3">
                  <Skeleton className="h-9 w-[100px]" />
                  <Skeleton className="h-9 w-[100px]" />
                  <Skeleton className="h-9 w-[100px]" />
                </div>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100 hover:bg-slate-100">
                  <TableHead className="w-[50px]"><Skeleton className="h-5 w-5 rounded" /></TableHead>
                  <TableHead className="py-3.5 text-slate-700 font-semibold text-sm w-[280px]">Şarkı</TableHead>
                  <TableHead className="py-3.5 text-slate-700 font-semibold text-sm">Sanatçı</TableHead>
                  <TableHead className="py-3.5 text-slate-700 font-semibold text-sm">Tür</TableHead>
                  <TableHead className="py-3.5 text-slate-700 font-semibold text-sm">Playlistler</TableHead>
                  <TableHead className="py-3.5 text-slate-700 font-semibold text-sm">Albüm</TableHead>
                  <TableHead className="py-3.5 text-slate-700 font-semibold text-sm">Süre</TableHead>
                  <TableHead className="py-3.5 text-slate-700 font-semibold text-sm">Eklenme Tarihi</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i} className="hover:bg-slate-50">
                    <TableCell><Skeleton className="h-5 w-5 rounded" /></TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <Skeleton className="h-5 w-[200px] mb-1" />
                        <Skeleton className="h-4 w-[150px]" />
                      </div>
                    </TableCell>
                    <TableCell><Skeleton className="h-5 w-[120px]" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-[120px]" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-[70px]" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-8 rounded" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="p-4 border-t border-slate-100 flex justify-between items-center">
              <Skeleton className="h-5 w-[150px]" />
              <div className="flex gap-1">
                <Skeleton className="h-8 w-8 rounded" />
                <Skeleton className="h-8 w-8 rounded" />
                <Skeleton className="h-8 w-8 rounded" />
                <Skeleton className="h-8 w-8 rounded" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
        <div
          className="flex"
          style={{
            background: 'linear-gradient(to right, #be185d, #db2777)',
          }}
        >
          <div className="p-8 flex justify-between items-start relative w-full">
            <div className="space-y-3 flex-1">
              <div className="text-white/80 text-sm font-medium">
                Müzik Yönetim Paneli
              </div>
              <h1 className="text-3xl font-bold text-white">
                Şarkı ve Albüm Kütüphanesi
              </h1>
              <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                Sisteme yeni şarkılar yükleyin, mevcut şarkıları düzenleyin ve
                organize edin. Müzik türlerini yönetin, playlist'lere şarkı ekleyin
                ve tüm müzik kütüphanenizi tek yerden kontrol edin.
              </p>
            </div>
            <div
              className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <Music className="w-20 h-20 text-white" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </div>

        {/* İstatistik Kartları */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statItems.map((item, index) => (
          <Card key={index} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
            <CardContent className="p-0">
              <div className="relative">
                {/* Arka plan dekoratif gradient */}
                <div className={`absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full ${item.bgColor} blur-2xl`}></div>

                <div className="p-6 relative">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{item.label}</p>
                      <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{item.value}</h3>
                    </div>
                    <div className={cn("p-3.5 rounded-xl shadow-sm", item.bgColor)}>
                      <item.icon className={cn("w-5 h-5", item.color)} strokeWidth={2.5} />
                    </div>
                  </div>

                  {/* Alt bilgi çubuğu */}
                  <div className="pt-3 border-t border-slate-100">
                    <div className="flex items-center text-xs text-slate-600">
                      {item.info}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        </div>

        {/* Filtre ve Arama Alanı */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="space-y-4">
            {/* Kaynak Filtresi Tab'ları */}
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <span className="text-sm font-medium text-slate-500 mr-2">Kaynak:</span>
              <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                <button
                  onClick={() => handleSourceChange('')}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2",
                    selectedSource === ''
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  )}
                >
                  <Music className="h-4 w-4" />
                  Tümü
                </button>
                <button
                  onClick={() => handleSourceChange('uploaded')}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2",
                    selectedSource === 'uploaded'
                      ? "bg-white text-blue-700 shadow-sm"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  )}
                >
                  <Upload className="h-4 w-4" />
                  Yüklenen
                </button>
                <button
                  onClick={() => handleSourceChange('ai-generated')}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-2",
                    selectedSource === 'ai-generated'
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  )}
                >
                  <Sparkles className="h-4 w-4" />
                  AI Üretim
                </button>
              </div>
            </div>

            <div className="flex gap-3 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Şarkı, sanatçı veya albüm ara..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-12 h-11 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={`h-11 px-5 rounded-lg border-slate-200 hover:bg-slate-50 ${selectedGenre ? 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100' : ''}`}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    {selectedGenre
                      ? (genres.find(g => g._id === selectedGenre)?.name || 'Tür')
                      : 'Tüm Türler'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[150px] max-h-[300px] overflow-y-auto">
                  <DropdownMenuItem
                    className="flex items-center gap-2 font-medium cursor-pointer"
                    onClick={() => handleGenreChange('')}
                  >
                    <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                    Tüm Türler
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />

                  {genres.length === 0 && (
                    <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                      Tür bulunamadı
                    </div>
                  )}

                  {genres.map((genre) => (
                    <DropdownMenuItem
                      key={genre._id}
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={() => handleGenreChange(genre._id)}
                    >
                      <span className="h-2 w-2 rounded-full bg-green-500"></span>
                      {genre.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                onClick={() => setShowUploader(true)}
                className="h-11 px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Şarkı Yükle
              </Button>
            </div>

            {/* Aktif filtre göstergesi */}
            {(selectedGenre || selectedSource) && (
              <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-100">
                <span className="text-xs font-medium text-slate-600 flex items-center">
                  Aktif Filtre:
                </span>
                {selectedSource && (
                  <Badge className={cn(
                    "flex items-center gap-1.5 px-3 py-1 border-0",
                    selectedSource === 'ai-generated'
                      ? "bg-purple-100 text-purple-800 hover:bg-purple-200"
                      : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                  )}>
                    {selectedSource === 'ai-generated' ? <Sparkles className="h-3 w-3" /> : <Upload className="h-3 w-3" />}
                    {selectedSource === 'ai-generated' ? 'AI Üretim' : 'Yüklenen'}
                    <X
                      className="h-3.5 w-3.5 ml-1 cursor-pointer hover:opacity-70"
                      onClick={() => handleSourceChange('')}
                    />
                  </Badge>
                )}
                {selectedGenre && (
                  <Badge className="flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 border-0 hover:bg-green-200">
                    <Filter className="h-3 w-3" />
                    {genres.find(g => g._id === selectedGenre)?.name}
                    <X
                      className="h-3.5 w-3.5 ml-1 cursor-pointer hover:text-green-600"
                      onClick={() => handleGenreChange('')}
                    />
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    handleGenreChange('');
                    handleSourceChange('');
                  }}
                  className="h-7 px-3 text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-200"
                >
                  Tümünü Temizle
                </Button>
              </div>
            )}

            {/* AI Üretim prompt panel — sadece AI filter aktifken görünür */}
            {selectedSource === 'ai-generated' && (
              <div className="p-5 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 border border-purple-200 rounded-xl space-y-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <h3 className="font-semibold text-slate-800">AI ile Müzik Üret</h3>
                  <Badge className="ml-auto bg-purple-100 text-purple-700 hover:bg-purple-200 border-0">
                    Super Admin
                  </Badge>
                </div>

                <Textarea
                  placeholder="Üretmek istediğin müziği açıkla... (örn: upbeat summer pop song, 120 BPM, female vocal, catchy hook)"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  disabled={isAiGenerating}
                  maxLength={300}
                  className="min-h-[90px] bg-white border-purple-200 focus:border-purple-400 focus:ring-purple-400 resize-none"
                />
                <div className="text-xs text-slate-500 text-right">
                  {aiPrompt.length} / 300
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Switch
                      checked={aiIsInstrumental}
                      onCheckedChange={setAiIsInstrumental}
                      disabled={isAiGenerating}
                    />
                    <span className="text-sm text-slate-700">Enstrümental</span>
                  </label>

                  <div className={`inline-flex rounded-lg border border-slate-200 overflow-hidden ${aiIsInstrumental ? 'opacity-40 pointer-events-none' : ''}`}>
                    <button
                      type="button"
                      onClick={() => setAiGender(aiGender === 'male' ? '' : 'male')}
                      disabled={aiIsInstrumental || isAiGenerating}
                      className={`px-3 py-2 text-xs font-medium transition-colors ${
                        aiGender === 'male' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Erkek
                    </button>
                    <button
                      type="button"
                      onClick={() => setAiGender(aiGender === 'female' ? '' : 'female')}
                      disabled={aiIsInstrumental || isAiGenerating}
                      className={`px-3 py-2 text-xs font-medium border-l border-slate-200 transition-colors ${
                        aiGender === 'female' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Kadın
                    </button>
                    <button
                      type="button"
                      onClick={() => setAiGender(aiGender === 'neutral' ? '' : 'neutral')}
                      disabled={aiIsInstrumental || isAiGenerating}
                      className={`px-3 py-2 text-xs font-medium border-l border-slate-200 transition-colors ${
                        aiGender === 'neutral' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Nötr
                    </button>
                  </div>

                  <Button
                    onClick={handleAiGenerate}
                    disabled={isAiGenerating || aiPrompt.trim().length === 0}
                    className="ml-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-sm disabled:opacity-50"
                  >
                    {isAiGenerating ? (
                      <>
                        <Clock3 className="h-4 w-4 mr-2 animate-spin" />
                        Başlatılıyor...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Üret
                      </>
                    )}
                  </Button>
                </div>

                {/* Bekleyen üretimler */}
                {pendingGenerations.length > 0 && (
                  <div className="pt-3 border-t border-purple-200 space-y-2">
                    <p className="text-xs font-medium text-purple-800">
                      Üretiliyor ({pendingGenerations.length})
                    </p>
                    {pendingGenerations.map((gen) => {
                      const elapsed = Math.floor((Date.now() - gen.startedAt) / 1000);
                      return (
                        <div
                          key={gen.id}
                          className="flex items-center gap-3 p-3 bg-white/70 border border-purple-100 rounded-lg"
                        >
                          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                            <Clock3 className="h-4 w-4 text-white animate-spin" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {gen.prompt}
                            </p>
                            <p className="text-xs text-slate-500">
                              {elapsed}s · MusicGPT üretimi devam ediyor...
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Dialog Bileşenleri */}
        <Dialog open={showUploader} onOpenChange={setShowUploader}>
        <DialogContent onPointerDownOutside={(e) => e.preventDefault()} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Şarkı Yükle</DialogTitle>
          </DialogHeader>
          <SongUploader onUploadComplete={handleUploadComplete} />
        </DialogContent>
      </Dialog>
      
      {/* Şarkı Düzenleme Modalı */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Şarkı Düzenle</DialogTitle>
            <DialogDescription>
              Şarkı bilgilerini düzenleyip kaydedebilirsiniz.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="songName" className="text-right">
                Şarkı Adı
              </Label>
              <Input
                id="songName"
                value={editFormData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditFormData({...editFormData, name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="artist" className="text-right">
                Sanatçı
              </Label>
              <Input
                id="artist"
                value={editFormData.artist}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditFormData({...editFormData, artist: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="album" className="text-right">
                Albüm
              </Label>
              <Input
                id="album"
                value={editFormData.album}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditFormData({...editFormData, album: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="genre" className="text-right">
                Tür
              </Label>
              <Select 
                value={editFormData.genre} 
                onValueChange={(value: string) => setEditFormData({...editFormData, genre: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tür seçin" />
                </SelectTrigger>
                <SelectContent>
                  {genres.map((genre) => (
                    <SelectItem key={genre._id} value={genre.name}>
                      {genre.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              İptal
            </Button>
            <Button onClick={handleSaveEdit}>
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        {/* Tablo */}
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
          {selectedSongs.length > 0 && (
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">
                    {selectedSongs.length} şarkı seçildi
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={handleCreatePlaylist} size="sm" className="h-9">
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Playlist
                  </Button>
                  <Button onClick={handleAddToPlaylist} variant="outline" size="sm" className="h-9">
                    <ListPlus className="h-4 w-4 mr-2" />
                    Playlist'e Ekle
                  </Button>
                  <Button onClick={handleRemoveFromPlaylist} variant="outline" size="sm" className="h-9">
                    <ListMinus className="h-4 w-4 mr-2" />
                    Çıkar
                  </Button>
                  <Button
                    onClick={handleBulkDelete}
                    variant="ghost"
                    size="sm"
                    className="h-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    Sil
                  </Button>
                  {selectedGenre && (
                    <Button
                      onClick={handleSelectAllByGenre}
                      variant="outline"
                      size="sm"
                      className="h-9"
                    >
                      <Music className="h-4 w-4 mr-2" />
                      Tüm {formatTitle(selectedGenre)} Seç
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tablo */}
          <Table className="w-full table-fixed">
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-50 hover:to-slate-100 border-b border-slate-200">
                <TableHead className="w-10 py-4 pl-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedSongs.length === filteredSongs.length && filteredSongs.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          clearSelection();
                          filteredSongs.forEach(song => addSong(song));
                        } else {
                          clearSelection();
                        }
                      }}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      aria-label="Tüm şarkıları seç"
                    />
                  </div>
                </TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm w-[280px]">Şarkı</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Sanatçı</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Tür</TableHead>
                {selectedSource === 'ai-generated' && (
                  <TableHead className="py-4 text-slate-700 font-bold text-sm">Firma</TableHead>
                )}
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Playlistler</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Albüm</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Süre</TableHead>
                <TableHead className="py-4 text-slate-700 font-bold text-sm">Eklenme Tarihi</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={selectedSource === 'ai-generated' ? 10 : 9} className="h-24 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredSongs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={selectedSource === 'ai-generated' ? 10 : 9} className="h-24 text-center">
                    <div className="flex flex-col items-center justify-center text-center py-6">
                      <FileX className="h-10 w-10 text-muted-foreground mb-2" />
                      <h3 className="text-lg font-medium">Şarkı Bulunamadı</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Arama kriterlerinize uygun şarkı bulunamadı.
                      </p>
                      <Button onClick={() => {setSearchQuery(''); setSelectedGenre('')}} variant="outline">
                        Filtreleri Temizle
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSongs.map((song, index) => (
                  <TableRow key={song._id} className="border-b border-slate-100 hover:bg-blue-50/30 transition-colors">
                    <TableCell className="pl-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedSongs.some(s => s._id === song._id)}
                        onChange={(e) => handleCheckboxChange(song, e.target.checked, index, e.nativeEvent)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 font-medium">
                        <div className="relative mr-1 group">
                          {song.artworkUrl ? (
                            <img 
                              src={song.artworkUrl.startsWith('https://') ? song.artworkUrl : `https://${song.artworkUrl}`} 
                              alt={song.name} 
                              className="h-10 w-10 rounded object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded bg-slate-200 flex items-center justify-center">
                              <Music className="h-5 w-5 text-slate-400" />
                            </div>
                          )}
                          <div 
                            className="absolute inset-0 bg-black bg-opacity-40 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation(); // Tablo satırı seçimini engelle
                              if (currentSong?._id === song._id && isPlaying) {
                                pauseSong();
                              } else {
                                playSong(song);
                              }
                            }}
                          >
                            {currentSong?._id === song._id && isPlaying ? (
                              <Pause className="h-5 w-5 text-white" />
                            ) : (
                              <Play className="h-5 w-5 text-white" />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {formatTitle(song.name)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{formatTitle(song.artist || '-')}</TableCell>
                    <TableCell className="genre-column">{formatTitle(song.genre || '-')}</TableCell>
                    {selectedSource === 'ai-generated' && (
                      <TableCell>
                        {song.companyName ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                            <Building2 className="h-3 w-3" />
                            {song.companyName}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {song.playlists && song.playlists.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {song.playlists.map((playlist) => (
                            <span 
                              key={playlist._id}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-primary/10 text-primary"
                              title={typeof playlist.name === 'string' ? playlist.name : ''}
                            >
                              {typeof playlist.name === 'string' && (
                                playlist.name.length > 15 
                                  ? `${playlist.name.substring(0, 15)}...` 
                                  : playlist.name
                              )}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>{formatTitle(song.album || '-')}</TableCell>
                    <TableCell>{song.duration ? formatTime(song.duration) : '-'}</TableCell>
                    <TableCell>
                      {new Date(song.createdAt).toLocaleDateString('tr-TR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                            <span className="sr-only">Menüyü aç</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={(e: Event) => {
                            e.preventDefault();
                            handleEdit(song);
                          }}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Düzenle
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDelete(song._id)}
                          >
                            <Trash className="mr-2 h-4 w-4" />
                            Sil
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Sayfalama */}
        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-slate-800">{pagination.total}</span> şarkı içinden{" "}
              <span className="font-semibold text-slate-800">{((pagination.page - 1) * 100) + 1}</span>-
              <span className="font-semibold text-slate-800">{Math.min(pagination.page * 100, pagination.total)}</span>{" "}
              arası gösteriliyor
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                aria-label="Önceki sayfa"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Önceki</span>
              </Button>

              <div className="flex items-center gap-1">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => {
                  // İlk sayfa, son sayfa veya current page etrafındaki 1 sayfa
                  if (
                    page === 1 ||
                    page === pagination.totalPages ||
                    (page >= pagination.page - 1 && page <= pagination.page + 1)
                  ) {
                    return (
                      <Button
                        key={page}
                        variant={pagination.page === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={`h-10 min-w-10 px-3 rounded-lg ${
                          pagination.page === page
                            ? "bg-blue-600 hover:bg-blue-700 text-white pointer-events-none"
                            : "border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {page}
                      </Button>
                    );
                  }

                  // Boşlukları göster
                  if (page === 2 && pagination.totalPages > 4) {
                    return <Button key="start-ellipsis" variant="outline" size="sm" disabled className="h-10 px-2 rounded-lg border-slate-200 pointer-events-none">...</Button>;
                  }

                  if (page === pagination.totalPages - 1 && pagination.totalPages > 4) {
                    return <Button key="end-ellipsis" variant="outline" size="sm" disabled className="h-10 px-2 rounded-lg border-slate-200 pointer-events-none">...</Button>;
                  }

                  return null;
                })}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="h-10 px-4 rounded-lg border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                aria-label="Sonraki sayfa"
              >
                <span className="hidden sm:inline">Sonraki</span>
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Mevcut Playlist'e Ekleme Modal'ı */}
        <AddToPlaylistModal 
        open={addToPlaylistOpen}
        onClose={() => setAddToPlaylistOpen(false)}
        selectedSongs={selectedSongs}
        onSuccess={handleAddToPlaylistSuccess}
      />
      
      {/* Playlist'ten Şarkı Çıkarma Modal'ı */}
      <RemoveFromPlaylistModal
        open={removeFromPlaylistOpen}
        onClose={() => setRemoveFromPlaylistOpen(false)}
        selectedSongs={selectedSongs}
        onSuccess={handleRemoveFromPlaylistSuccess}
      />
      </div>
    </div>
  );
}
